import { ArrowBack } from "@mui/icons-material";
import { Divider, IconButton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { MapPanelHeader, MapPanelScrollableArea } from "~/modules/map/components/MapPanel";
import LinkableItemList from "~/modules/mapEditor/components/LinkableItemList";
import MapEditorFeatureDetailTitle from "~/modules/mapEditor/components/MapEditorFeatureDetailTitle";
import { ObservationCard } from "~/modules/mapEditor/components/ObservationCard";
import { ObservationMediaDisplay } from "~/modules/mapEditor/components/ObservationMediaDisplay";
import FormTemplateDialogEditor from "~/shared/components/FormTemplate/FormTemplateDialogEditor";
import FormTemplateForm from "~/shared/components/FormTemplate/FormTemplateForm";

import { MapEditorEcologicalFunctionPanelEcologicalFunctionFragment } from "./fragments.generated";
import { useSuspenseEcologicalFunctionReadOnlyQuery } from "./useSuspenseEcologicalFunctionReadOnlyQuery";

export interface MapEditorEcologicalFunctionPanelReadOnlyProps {
    backHref: string;
    mapEditorFeatureId: string;
    title: string;
}

export function MapEditorEcologicalFunctionPanelReadOnly({
    backHref,
    mapEditorFeatureId,
    title,
}: MapEditorEcologicalFunctionPanelReadOnlyProps) {
    const { t } = useTranslation("mapEditor");

    const { data } = useSuspenseEcologicalFunctionReadOnlyQuery(mapEditorFeatureId);
    const ecologicalFunction = data.mapEditorFeatureGetById.data as MapEditorEcologicalFunctionPanelEcologicalFunctionFragment;

    return (
        <>
            <MapPanelHeader>
                <IconButton component={Link} color="inherit" relative="path" to={backHref}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h6">{title}</Typography>
            </MapPanelHeader>
            <MapPanelScrollableArea>
                <Stack gap={2} padding={2}>
                    <MapEditorFeatureDetailTitle
                        title={ecologicalFunction.researchType.name}
                        user={ecologicalFunction.createdByUser}
                    />

                    <Stack>
                        <Typography variant="caption">{t("featureDetailEcologicalFunction.ecologicalFunctionType")}</Typography>
                        <Typography>{ecologicalFunction.type.name}</Typography>
                    </Stack>

                    <Stack>
                        <Typography variant="caption">{t("featureDetailEcologicalFunction.species")}</Typography>
                        <Typography>
                            {ecologicalFunction.speciesList.map(speciesListItem => speciesListItem.name).join(", ")}
                        </Typography>
                    </Stack>

                    <Stack>
                        <Typography variant="caption">{t("featureDetailEcologicalFunction.createdAt")}</Typography>
                        <Typography>{parseStringToDate(ecologicalFunction.createdAt).toLocaleString()}</Typography>
                    </Stack>

                    {ecologicalFunction.medias.length > 0 && (
                        <Stack>
                            <Typography variant="caption">{t("featureDetailEcologicalFunction.media")}</Typography>
                            <ObservationMediaDisplay media={ecologicalFunction.medias} />
                        </Stack>
                    )}

                    {ecologicalFunction.preciseLocation && (
                        <>
                            <Divider>
                                <Typography>{t("featureDetailEcologicalFunction.preciseLocation")}</Typography>
                            </Divider>
                            <FormTemplateDialogEditor
                                form={data.settingsFormTemplateGetMasterForm}
                                values={ecologicalFunction.preciseLocation}
                                onChange={() => {}}
                                readOnly={true}
                            />
                        </>
                    )}

                    {ecologicalFunction.type.formTemplate && (
                        <>
                            <Divider>
                                <Typography>{t("featureDetailEcologicalFunction.ecologicalFunctionDetails")}</Typography>
                            </Divider>
                            <FormTemplateForm
                                form={ecologicalFunction.type.formTemplate}
                                values={ecologicalFunction.answers ?? {}}
                                onChange={() => {}}
                                readOnly={true}
                            />
                        </>
                    )}

                    {ecologicalFunction.observations.length > 0 && (
                        <>
                            <Divider>{t("observationLinking.dividerLinkedObservations")}</Divider>
                            <LinkableItemList
                                clickable={false}
                                items={ecologicalFunction.observations}
                                renderCard={observation => <ObservationCard observation={observation} />}
                            />
                        </>
                    )}
                </Stack>
            </MapPanelScrollableArea>
        </>
    );
}
